import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import { InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyGetTicketListForVendorProfileQuery } from "store/services/ticketApi";
import { useAuth, useTablePagination } from "hooks";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useCustomParams } from "hooks/useCustomParams";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const VendorTicketList = ({ vendorId }) => {
  const { isEntityAdmin, isVendor, isVendorStaff } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { appendParams } = useCustomParams();

  const activeTab = searchParams.get("tab") || "Open";
  const keyword = searchParams.get("search") || "";
  const responsible = searchParams.get("responsible") || null;
  const ticketType = searchParams.get("ticketType") || "All";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [
    fetchTickets,
    { data: ticketRes, isLoading: loadingTickets, isFetching: fetchingTickets },
  ] = useLazyGetTicketListForVendorProfileQuery();

  const ticketTabs = [
    {
      label: "Open",
      value: "Open",
    },
    {
      label: "In Progress",
      value: "In Progress",
    },
    {
      label: "Closed",
      value: "Closed",
    },
    {
      label: "All",
      value: "All",
    },
    {
      label: "Inactive",
      value: "Inactive",
    },
  ];

  const {
    data: ticketTypes = [],
    isLoading: isLoadingTicketTypes,
    isFetching: isFetchingTicketTypes,
  } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.TICKET_TYPE,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const _mappedTicketTypes = useMemo(
    () => [
      { label: "All", _id: "All" },
      ...(Array.isArray(ticketTypes)
        ? ticketTypes.map((item) => ({
            label: item?.name || "",
            _id: item?._id || "",
          }))
        : []),
    ],
    [ticketTypes]
  );

  const columns = [
    {
      name: "Subject",
      cell: (row) => (
        <Link
          to={
            isVendor
              ? `/app/vendor/ticket/${row?._id}/detail?prevTab=${activeTab}`
              : isVendorStaff
              ? `/app/staff/ticket/${row?._id}/detail?prevTab=${activeTab}`
              : `/app/crm/ticket/${row?._id}/detail?prevTab=${activeTab}`
          }
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          title={row?.subject}
        >
          <span className="text-blue-500 dark:text-blue-200">
            #{row?.ticketId}
          </span>{" "}
          <HighlightedText text={row?.subject} highlight={keyword} />
        </Link>
      ),
      minWidth: "250px",
    },
    ...(!isEntityAdmin && !isVendor
      ? [
          {
            name: "Entity",
            cell: (row) => (
              <div
                title={row?.entity?.displayName ?? row?.entity?.name}
                className="line-clamp-2"
              >
                {row?.entity?.displayName ?? row?.entity?.name}
              </div>
            ),
          },
        ]
      : []),
    {
      name: "Source",
      cell: (row) =>
        row?.sourceRef ? (
          isVendor ? (
            <div className="flex flex-col gap-1">
              <div> {row?.sourceRef?.ownerName}</div>
              <div className="text-xs font-medium text-blue-500">
                {row?.source && row.source.replace(/([a-z])([A-Z])/g, "$1 $2")}
              </div>
            </div>
          ) : (
            <OpenInNewTab
              label={row?.sourceRef?.ownerName}
              link={`/app/crm/vendor-profile/${row?.sourceRef?._id}/detail`}
              disableIcon
              disableColor
              disableHover
            >
              <div className="flex flex-col gap-1">
                <div> {row?.sourceRef?.ownerName}</div>
                <div className="text-xs font-medium text-blue-500">
                  {row?.source === "VendorProfile" && "Vendor Profile"}
                </div>
              </div>
            </OpenInNewTab>
          )
        ) : (
          "N/A"
        ),
    },

    {
      name: "Type",
      cell: (row) => (
        <div title={row?.ticketType?.name} className="line-clamp-2">
          {row?.ticketType?.name ?? "N/A"}
        </div>
      ),
    },

    ...(activeTab === "All"
      ? [
          {
            name: "Stage",
            selector: (row) => row?.stage,
          },
        ]
      : []),
    {
      name: "Created At",
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    ...(activeTab === "Closed"
      ? [
          {
            name: "Closed At",
            cell: (row) => (
              <div className="grid grid-cols-1 gap-1 py-1">
                <AppTableDateTime date={row?.closedAt} />
                {row?.closedBy && (
                  <div className="text-sm text-gray-500 dark:text-gray-200">
                    by {row?.closedBy?.name}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      name: "Responsible",
      cell: (row) => (
        <div title={row?.responsible?.name} className="line-clamp-2">
          {row?.responsible?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Created By",
      cell: (row) => (
        <div title={row?.createdBy?.name} className="line-clamp-2">
          {row?.createdBy?.name ?? "N/A"}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" alignItems="center">
          <Link
            to={
              isVendor
                ? `/app/vendor/ticket/${row?._id}/detail?prevTab=${activeTab}`
                : isVendorStaff
                ? `/app/staff/ticket/${row?._id}/detail?prevTab=${activeTab}`
                : `/app/crm/ticket/${row?._id}/detail?prevTab=${activeTab}`
            }
          >
            <IconButton size="small" aria-label="Ticket">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
          <Link
            to={
              isVendor
                ? `/app/vendor/ticket/${row?._id}/edit`
                : isVendorStaff
                ? `/app/staff/ticket/${row?._id}/edit`
                : `/app/crm/ticket/${row?._id}/edit`
            }
          >
            <IconButton size="small" aria-label="Edit Ticket">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  // Handle tab changes
  const handleTabChange = (newTab) => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set("tab", newTab);
    setSearchParams(currentParams);
  };

  useEffect(() => {
    if (!vendorId) return;

    // Determine the parameters based on the active tab
    const getTicketParams = () => {
      const baseParams = {
        vendorId,
        page: currentPage,
        limit: perPage,
        ...(ticketType && ticketType !== "All" && { ticketType }),
        ...(keyword && { keyword }),
        ...(responsible && { responsible }),
      };

      // Handle different ticket statuses
      const ticketStatuses = [
        "Open",
        "In Progress",
        "Closed",
        "All",
        "Inactive",
      ];

      if (ticketStatuses.includes(activeTab)) {
        // If it's a ticket status tab
        if (activeTab === "Inactive") {
          baseParams.active = false;
        } else if (activeTab === "All") {
          baseParams.active = true;
          // Don't filter by stage for "All"
        } else {
          baseParams.active = true;
          baseParams.stage = activeTab;
        }
      } else {
        // Default behavior for "ticket" tab (show Open tickets)
        baseParams.active = true;
        baseParams.stage = "Open";
      }

      return baseParams;
    };

    fetchTickets(getTicketParams());
  }, [
    vendorId,
    activeTab,
    currentPage,
    perPage,
    keyword,
    responsible,
    fetchTickets,
    ticketType,
  ]);

  return (
    <>
      <CustomTabs
        tabs={ticketTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
      <TableFilter
        searchLabel="Subject"
        disabled={loadingTickets || fetchingTickets}
      >
        <BaseSelect
          fullWidth
          label="Types"
          value={ticketType}
          onChange={(e) => {
            appendParams({
              ticketType: e.target.value,
            });
          }}
          options={_mappedTicketTypes}
          isLoading={isLoadingTicketTypes || isFetchingTicketTypes}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt._id}
        />
      </TableFilter>

      <AppTable
        columns={columns}
        data={ticketRes?.data ?? []}
        progressPending={loadingTickets || fetchingTickets}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={ticketRes?.totalItems ?? 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </>
  );
};

export default VendorTicketList;
